import { CellContext, createColumnHelper } from '@tanstack/react-table'
import { NotEnrolledDeviceDetails, EnrolledDeviceInformation } from '../../types/DeviceTypes'
import { Button, Skeleton, Tooltip } from '@radix-ui/themes'
import { formatUtcTimestamp } from '../../utils/dates'
import './styles.css'
import Scroll from '../scroll/Scroll'
import {
  IconAlertHexagon,
  IconAlertTriangle,
  IconBrandAndroid,
  IconBrandApple,
  IconCircleCheck,
  IconCode,
  IconDownload,
  IconHandStop,
  IconHelpCircle,
  IconLoader,
  IconShield,
  IconShieldOff,
  IconShieldQuestion,
  IconShieldStar,
  IconUsb,
} from '@tabler/icons-react'
import { generateDemoView } from '../alert-console/AlertColumns'

export const deviceColumns = (
  checkedItems: Record<string, boolean>,
  setCheckedItems: (checkedItems: Record<string, boolean>) => void,
  handleCheckboxChange: (accessCode: string) => void,
  setNavigateToDevicePage: (show: boolean) => void,
  setPopoutData: (deviceInfo: EnrolledDeviceInformation) => void,
  vpnScanning: boolean
) => {
  const columnHelper = createColumnHelper<EnrolledDeviceInformation>()

  const Empty = () => <Skeleton className="h-6" />

  return [
    {
      id: 'selection',
      cell: (row: CellContext<EnrolledDeviceInformation, string>) => {
        return (
          <div className="ml-4 mt-1.5">
            <input
              key={row.row.original.accessCode}
              id={row.row.original.accessCode}
              type="checkbox"
              checked={checkedItems[row.row.original.accessCode] || false}
              onChange={() => {
                setCheckedItems({
                  ...checkedItems,
                  [row.row.original.accessCode]: !checkedItems[row.row.original.accessCode],
                })
                handleCheckboxChange(row.row.original.accessCode)
              }}
              className="h-8 w-8 text-blue-600 border-gray-800 rounded focus:ring-mainPurple checked:bg-mainPurple dark:border-gray-200 dark:bg-transparent dark:checked:bg-mainPurple scale-50"
            />
          </div>
        )
      },
    },
    columnHelper.accessor('name', {
      header: 'Name',
      maxSize: 50,
      cell: (row) => {
        return generateDemoView(
          row.row.original.deviceName === null ? row.row.original.name : row.row.original.deviceName,
          row.row.original.demoDevice ?? false,
          'device',
          row.row.original.hasOpenAlerts
        )
      },
      enableSorting: true,
    }),
    columnHelper.accessor('riskScore', {
      header: 'Risk Score',
      cell: (info) => {
        const riskScore = info.getValue()
        return info.row.original.hasOpenAlerts === null ? (
          <Empty />
        ) : (
          convertRiskScore(riskScore !== null ? riskScore : -1)
        )
      },
      enableSorting: true,
    }),
    columnHelper.accessor('hasOpenAlerts', {
      header: 'Threat Status',
      cell: (info) => (info.row.original.hasOpenAlerts === null ? <Empty /> : convertStatus(info.getValue())),
      enableSorting: true,
    }),
    columnHelper.accessor('model', {
      header: 'Model',
      cell: (info) => {
        return info.row.original.hasOpenAlerts === null ? <Empty /> : info.getValue() ? info.getValue() : 'Unknown'
      },
      enableSorting: true,
    }),
    columnHelper.accessor('os', {
      cell: (row) =>
        row.row.original.hasOpenAlerts === null ? (
          <Empty />
        ) : (
          convertLatestOs(
            row.row.original.os,
            row.row.original.osVersionState,
            row.row.original.securityPatchLastUpdatedData,
            row.row.original.latestOSVersion,
            row.row.original.platform
          )
        ),
      header: 'Version',
      enableSorting: false,
    }),
    columnHelper.accessor('smishingLastUpdatedData', {
      header: 'Smishing',
      cell: (info) => (info.row.original.hasOpenAlerts === null ? <Empty /> : convertBoolean(info.getValue(), true)),
      enableSorting: true,
    }),
    columnHelper.accessor('biometricsLastUpdatedData', {
      header: 'Biometrics',
      cell: (info) => (info.row.original.hasOpenAlerts === null ? <Empty /> : convertBoolean(info.getValue(), true)),
      enableSorting: true,
    }),
    columnHelper.accessor('screenLockLastUpdatedData', {
      header: 'Screen Lock',
      cell: (info) => (info.row.original.hasOpenAlerts === null ? <Empty /> : convertBoolean(info.getValue(), true)),
      enableSorting: true,
    }),
    columnHelper.accessor('iosLockdownUpdatedData', {
      cell: (row) => {
        return generateDeviceSecurityView(row.row.original, vpnScanning)
      },
      header: 'Security',
      enableSorting: false,
    }),
    columnHelper.accessor('latestScanDate', {
      cell: (info) => {
        return info.row.original.hasOpenAlerts === null ? (
          <Empty />
        ) : (
          formatUtcTimestamp(info.getValue())
        )
      },
      header: 'Last Checked (UTC)',
      enableSorting: true,
    }),
    {
      id: 'button',
      cell: (row: { row: { original: EnrolledDeviceInformation } }) =>
        row.row.original.hasOpenAlerts === null ? (
          <></>
        ) : (
          <Button
            size={'2'}
            className="cursor-pointer float-right"
            onClick={() => {
              setPopoutData(row.row.original)
              setNavigateToDevicePage(true)
            }}
            variant="outline"
          >
            View
          </Button>
        ),
    },
  ]
}

export const notActivatedDeviceColumns = (
  checkedItems: Record<string, boolean>,
  setCheckedItems: (checkedItems: Record<string, boolean>) => void,
  handleCheckboxChange: (accessCode: string) => void
) => {
  const columnHelper = createColumnHelper<NotEnrolledDeviceDetails>()

  return [
    columnHelper.accessor('name', {
      header: () => <span className="ml-10">Name</span>,
      maxSize: 50,
      cell: (row: CellContext<NotEnrolledDeviceDetails, string>) => {
        return (
          <div className="flex">
            <div className="ml-4 flex">
              <input
                key={row.row.original.accessCode}
                id={row.row.original.accessCode}
                type="checkbox"
                className="h-8 w-8 mt-2.5 text-blue-600 border-gray-800 rounded focus:ring-mainPurple checked:bg-mainPurple dark:border-gray-200 dark:bg-transparent dark:checked:bg-mainPurple scale-50"
                checked={checkedItems[row.row.original.accessCode] || false}
                onChange={() => {
                  setCheckedItems({
                    ...checkedItems,
                    [row.row.original.accessCode]: !checkedItems[row.row.original.accessCode],
                  })
                  handleCheckboxChange(row.row.original.accessCode)
                }}
              />

              <Scroll classNames="my-2 pb-1 ml-3">
                <p>{row.row.original.deviceName === null ? row.row.original.name : row.row.original.deviceName}</p>
                <p className="text-tableAccessGrey text-xs">{row.row.original.accessCode}</p>
              </Scroll>
            </div>
          </div>
        )
      },
    }),
    columnHelper.accessor('createdAt', {
      enableSorting: true,
      header: 'Created At (UTC)',
      cell: (row: CellContext<NotEnrolledDeviceDetails, string>) => {
        return (
          <div className="flex justify-end">
            <div className="text-left min-w-[150px]">
              {row.row.original.createdAt === null
                ? 'Unknown'
                : formatUtcTimestamp(row.row.original.createdAt)}
            </div>
          </div>
        )
      },
    }),
  ]
}

export const convertBoolean = (value: boolean | null, active: boolean) => {
  if (!active || value === null) {
    return (
      <div className="flex text-neutral-11 dark:text-neutral-9">
        <IconShieldQuestion size={20} className="mr-1" />
        Unknown
      </div>
    )
  }

  return value ? (
    <div className="flex text-green-700">
      <IconShield size={20} className="mr-1" />
      <span className="text-mainText">On</span>
    </div>
  ) : (
    <div className="flex text-red-700">
      <IconShieldOff size={20} className="mr-1" />
      <span className="text-mainText">Off</span>
    </div>
  )
}

export const convertSystem = (value: string) => {
  return value === 'IOS' ? <IconBrandApple size={20} /> : <IconBrandAndroid size={20} />
}

export const convertStatus = (value: boolean | null) => {
  switch (value) {
    case false:
      return (
        <div className="flex text-green-700">
          <IconCircleCheck size={20} className="mr-1" />
          <span className="text-mainText">Clear</span>
        </div>
      )
    case true:
      return (
        <div className="flex text-red-700">
          <IconAlertTriangle size={20} className="mr-1" />
          <span className="text-mainText">Detection</span>
        </div>
      )
    default:
      return (
        <div className="flex text-mainText">
          <IconHelpCircle size={20} className="mr-1" />
          Unknown
        </div>
      )
  }
}

export const convertLatestOs = (
  os: string,
  osVersionState: string,
  securityPatchVersion: string,
  latestOSVersion: string,
  platform: string
) => {
  if (osVersionState === 'OUT_OF_DATE') {
    return (
      <Tooltip content={<p>iOS {latestOSVersion} is available for update</p>}>
        <div className="flex text-red-700">
          {platform === 'IOS' ? (
            <IconBrandApple size={20} className="mr-1" />
          ) : (
            <IconBrandAndroid size={20} className="mr-1" />
          )}
          <p className="text-mainText">{removeOsFromVersion(os)}</p>
        </div>
      </Tooltip>
    )
  } else if (osVersionState === 'SECURITY_PATCH_OUTDATED') {
    return (
      <Tooltip
        content={
          <p>
            Android security patch version {latestOSVersion} has been released. This device is running{' '}
            {securityPatchVersion}.
          </p>
        }
      >
        <div className="flex text-red-700">
          {platform === 'IOS' ? (
            <IconBrandApple size={20} className="mr-1" />
          ) : (
            <IconBrandAndroid size={20} className="mr-1" />
          )}
          <p className="text-mainText">{removeOsFromVersion(os)}</p>
        </div>
      </Tooltip>
    )
  } else if (osVersionState === 'LATEST_FOR_DEVICE') {
    return (
      <Tooltip content={<p>This is an old OS version but the maximum version available for this device.</p>}>
        <div className="flex text-yellow-600">
          {platform === 'IOS' ? (
            <IconBrandApple size={20} className="mr-1" />
          ) : (
            <IconBrandAndroid size={20} className="mr-1" />
          )}
          <p className="text-mainText">{removeOsFromVersion(os)}</p>
        </div>
      </Tooltip>
    )
  } else {
    return (
      <div className="flex text-green-700">
        {platform === 'IOS' ? (
          <IconBrandApple size={20} className="mr-1" />
        ) : (
          <IconBrandAndroid size={20} className="mr-1" />
        )}
        <p className="text-mainText">{removeOsFromVersion(os)}</p>
      </div>
    )
  }
}

export const removeOsFromVersion = (os: string) => {
  return os ? os?.split(' ')[1] : 'Unknown'
}

export const convertRiskScore = (value: number) => {
  let backgroundColour = '#EBEBEF'
  let textColour = '#60646C'

  if (value === -1) {
    backgroundColour = 'bg-neutral-4'
    textColour = 'text-neutral-11'
  } else if (value >= 0 && value < 25) {
    backgroundColour = 'bg-backgroundGreen'
    textColour = 'text-green-700 dark:text-green-500'
  } else if (value >= 25 && value < 75) {
    backgroundColour = 'bg-backgroundYellow'
    textColour = 'text-textYellow'
  } else if (value >= 75) {
    backgroundColour = 'bg-backgroundRed'
    textColour = 'text-red-400'
  }

  return (
    <div
      className={`h-6 w-[42px] flex items-center justify-center rounded ${backgroundColour} ${textColour}`}
    >
      {value === -1 ? (
        <Tooltip content={'A risk score has not been calculated for this device yet. This can take up to 12 hours.'}>
          <IconLoader size={20} />
        </Tooltip>
      ) : (
        value
      )}
    </div>
  )
}

export const generateDeviceSecurityView = (value: EnrolledDeviceInformation, vpnScanning: boolean) => {
  if (value.platform === 'IOS') {
    const showLockdown = value.iosLockdownUpdatedData === true
    const showElte = vpnScanning && value.eliteMonitoringTokenHash !== null

    return (
      <div className="flex gap-2">
        {showElte && (
          <Tooltip content="Extended Protection On">
            <IconShieldStar size={20} className="mr-0.5 text-green-700 cursor-pointer" />
          </Tooltip>
        )}
        {showLockdown && (
          <Tooltip content="Lockdown Mode On">
            <IconHandStop size={20} className="mr-0.5 text-green-700 cursor-pointer" />
          </Tooltip>
        )}
      </div>
    )
  } else {
    const showPlayProtect = value.androidPlayProtectEnabledUpdatedData === false
    const showDebugBridge = value.androidAdbEnabledUpdatedData === true
    const showRootDetection = value.androidRootDetectedUpdatedData === true
    const sideLoading = value.androidSideloadingEnabledUpdatedData === true
    const developerMode = value.androidDeveloperModeEnabledUpdatedData === true

    return (
      <div className="flex gap-2">
        {showPlayProtect && (
          <Tooltip content="Play Protect Off">
            <IconShield size={20} className="mr-0.5 text-red-700 cursor-pointer" />
          </Tooltip>
        )}
        {showDebugBridge && (
          <Tooltip content="Debug Bridge On">
            <IconUsb size={20} className="mr-0.5 text-red-700 cursor-pointer" />
          </Tooltip>
        )}
        {showRootDetection && (
          <Tooltip content="Root Detection">
            <IconAlertHexagon size={20} className="mr-0.5 text-red-700 cursor-pointer" />
          </Tooltip>
        )}
        {sideLoading && (
          <Tooltip content="Side Loading On">
            <IconDownload size={20} className="mr-0.5 text-red-700 cursor-pointer" />
          </Tooltip>
        )}
        {developerMode && (
          <Tooltip content="Developer Mode On">
            <IconCode size={20} className="mr-0.5 text-neutral-11 cursor-pointer" />
          </Tooltip>
        )}
      </div>
    )
  }
}
